import type { Webinar } from '@domain/models/webinar'
import { extractYouTubeVideoId } from '@presentation/helpers/webinar'
import { buildBreadcrumbList } from '@presentation/seo/StructuredDataBuilders'
import { toAbsoluteUrl } from '@presentation/seo/seo-constants'

type StructuredDataNode = Record<string, unknown>

interface BuildWebinarStructuredDataInput {
  webinar: Webinar
  canonicalPath: string
  webinarDescription: string
  ogImageUrl?: string
  isUpcoming: boolean
}

function normalizeOptionalString(value: string | undefined): string | undefined {
  const normalized = value?.trim()
  return normalized && normalized.length > 0 ? normalized : undefined
}

export function buildWebinarStructuredData({
  webinar,
  canonicalPath,
  webinarDescription,
  ogImageUrl,
  isUpcoming,
}: BuildWebinarStructuredDataInput): StructuredDataNode[] {
  const canonicalUrl = toAbsoluteUrl(canonicalPath)
  const normalizedYouTubeUrl = normalizeOptionalString(webinar.youtubeUrl)
  const normalizedRegisterUrl = normalizeOptionalString(webinar.registerUrl)
  const normalizedImageUrl = normalizeOptionalString(ogImageUrl)
  const videoId = extractYouTubeVideoId(normalizedYouTubeUrl)

  const schemas: StructuredDataNode[] = [
    buildBreadcrumbList([
      { name: 'Home', pathOrUrl: '/' },
      { name: 'Webinars', pathOrUrl: '/webinars/latest' },
      { name: webinar.title, pathOrUrl: canonicalPath },
    ]),
  ]

  if (normalizedYouTubeUrl) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: webinar.title,
      description: webinarDescription,
      url: canonicalUrl,
      uploadDate: webinar.webinarDate.toISOString(),
      thumbnailUrl: normalizedImageUrl ? [toAbsoluteUrl(normalizedImageUrl)] : undefined,
      embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}` : undefined,
      contentUrl: normalizedYouTubeUrl,
    })
  }

  if (isUpcoming) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: webinar.title,
      description: webinarDescription,
      startDate: webinar.webinarDate.toISOString(),
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      location: {
        '@type': 'VirtualLocation',
        url: normalizedRegisterUrl || canonicalUrl,
      },
      organizer: {
        '@type': 'Organization',
        name: "Parkinson's Insights",
        url: toAbsoluteUrl('/'),
      },
      url: canonicalUrl,
    })
  }

  return schemas
}
